import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronRight } from 'lucide-react';

import { homeTiles, tileDate, tileSpans } from './homeTiles';
import {
  Card,
  MetricBreathIcon,
  MetricFatigueIcon,
  MetricFormantIcon,
  MetricPitchIcon,
  MetricRangeIcon,
  MetricResonanceIcon,
  MetricVibratoIcon,
  SectionHeadCard,
  TransitionLink,
} from '../../components';

import type { DiagnosisRecord } from '../onboarding/record';
import type { MetricTabId } from '../onboarding/resultCopy';

/**
 * 지표 타일 일곱(시안 §7.1) — 마지막 회차의 한눈 요약이자 상세로 가는 문.
 *
 * **타일과 상세 탭이 같은 목록이다.** 이름도 순서도 `ResultTabs`와 같은 것을 쓰고, 누르면
 * 그 탭으로 간다(`/result/:id/:tab`). 홈에서 셋째로 본 지표가 상세에서 다섯째면 같은
 * 목록이라는 걸 못 읽는다.
 *
 * 배치는 시안대로 **4 + 3** — 첫 줄 넷, 둘째 줄 셋, 간격 6px. 타일은 흰색·라운드 12·
 * 그림자, 라벨 11px(BK_600) 위에 값 15px SemiBold, 우하단에 32px 회색 원 안 지표 아이콘.
 * 못 잰 타일은 목록에 없어서(`homeTiles`) 회차마다 수가 다르다 — 줄 나눔은 `tileSpans`가
 * 센다.
 *
 * 헤더는 카드 한 장(`최근 진단 결과` + `2026. 00. 00 >`) — 날짜는 그 회차의 날짜이자
 * 이력으로 가는 링크다.
 *
 * **못 잰 지표는 타일이 없다.** 시안은 그 자리를 `00`으로 그려 뒀지만 그건 자리표시자고,
 * 빈 타일은 눌러도 빈 탭으로 갈 뿐이었다. 판정은 `homeTiles`가 한다.
 */

/**
 * 타일 우하단 아이콘 — 시안 `1014:9946`에서 내보낸 7종. 색이 뜻을 실어(레드/오렌지) 그대로
 * 둔다(`make-icon-components.py`가 `currentColor`로 안 바꾼 이유).
 */
export const METRIC_ICON: Record<
  MetricTabId,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  fatigue: MetricFatigueIcon,
  registers: MetricPitchIcon,
  range: MetricRangeIcon,
  vibrato: MetricVibratoIcon,
  breath: MetricBreathIcon,
  formant: MetricFormantIcon,
  resonance: MetricResonanceIcon,
};

/**
 * `tileSpans`가 내는 칸 수 → Tailwind 클래스. 문자열을 조립하지 않는다 — JIT은 소스에
 * 통째로 적힌 클래스만 만들어서 `col-span-${n}`은 스타일이 안 붙는다.
 */
const SPAN_CLASS: Record<number, string> = {
  3: 'col-span-3',
  4: 'col-span-4',
  6: 'col-span-6',
  12: 'col-span-12',
};

export const MetricTiles = ({
  record,
  history = [],
}: {
  record: DiagnosisRecord;
  /** 저장된 회차 전부. 피로도 타일만 쓴다 — 그 값이 이력에서 나온다. */
  history?: readonly DiagnosisRecord[];
}) => {
  const { t } = useTranslation();
  const tiles = homeTiles(record, history);
  const spans = tileSpans(tiles.length);
  const date = tileDate(record.completedAt);

  // 잰 지표가 하나도 없는 회차에는 머리 카드까지 안 세운다 — 제목만 남은 빈 구역이
  // 된다.
  if (tiles.length === 0) {
    return null;
  }

  return (
    <section className="mt-8">
      {/* 오른쪽 부속은 **가는 곳을 말한다.** 예전에는 회차 날짜 + 쉐브론이라 눈으로는
          "이 회차 열기"로 읽히는데 실제로는 `/history` 목록으로 갔다(시안 20260730
          §7.2 는 이미 `결과 전체 보기 >` 로 바꿔 놨다). 날짜는 정보라 지우지 않고
          제목 아래 캡션으로 내린다 — 누를 수 없는 글자면 약속을 하지 않는다. */}
      <SectionHeadCard
        title={
          <>
            {t('web.home.tiles.section')}
            <span className="mt-0.5 block text-[13px] font-normal tracking-[-0.065px] text-bk-600">
              {t(date.key, date.params)}
            </span>
          </>
        }
        right={
          <TransitionLink
            to="/history"
            className="flex min-h-[44px] items-center gap-0.5 text-[15px] tracking-[-0.075px] text-bk-600"
          >
            {t('web.home.recent.all')}
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </TransitionLink>
        }
      />

      <ul
        aria-label={t('web.home.tiles.section')}
        className="mt-6 grid grid-cols-12 gap-1.5"
      >
        {tiles.map(({ tab, value, valueKey, valueParams }, i) => {
          const Icon = METRIC_ICON[tab];
          const body = (
            <Card className="flex h-full flex-col gap-2 rounded-xl px-2 py-3">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] leading-tight tracking-[-0.055px] text-bk-600">
                  {t(`web.result.tabs.name.${tab}`)}
                </span>
                {/* 타일이 좁다(4열 ≈ 78px). 값은 한 줄에 — `E2-C5`가 하이픈에서 꺾이면 음역이
                        두 줄로 갈라져 읽힌다. */}
                <span className="whitespace-nowrap text-[15px] font-semibold tracking-[-0.075px] tabular-nums text-bk-900">
                  {valueKey ? t(valueKey, valueParams) : value}
                </span>
              </div>
              <span
                aria-hidden="true"
                className="ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-bk-50"
              >
                <Icon className="h-[18px] w-[18px]" />
              </span>
            </Card>
          );
          return (
            <li key={tab} className={SPAN_CLASS[spans[i]]}>
              <TransitionLink
                to={`/result/${record.id}/${tab}`}
                className="block h-full"
              >
                {body}
              </TransitionLink>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
